import { memo, useState } from 'react';
import { Box, Button, TextField, MenuItem } from '@mui/material';
import _map from 'lodash/map';
import { useNavigate } from 'react-router-dom';
// Local files
import BaseDialog from 'components/Common/Feedback/BaseDialog/BaseDialog';
import BaseDatePicker from 'components/Common/BaseDatePicker/BaseDatePicker';
import PhoneField from 'components/Common/PhoneField/PhoneField';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import usePatients from 'hooks/usePatients';
import { checkValidEmail, checkEmptyString, getDateWithNullTimeZone } from 'helpers';
import { languages, modes, triggers } from 'helpers/constants';
import { patientsSelectors } from 'features/patients';

const today = new Date();

const Patient = ({ open, onClose }) => {
  const navigate = useNavigate();
  const filter = useCustomSelector(patientsSelectors.getPatientsSource);
  const fetching = useCustomSelector(patientsSelectors.patientIsFetching);
  const { setSuccess } = useSuccess();
  const { createPatient } = usePatients();
  const [firstName, setFirstName] = useState({ value: '', error: '' });
  const [lastName, setLastName] = useState({ value: '', error: '' });
  const [phone, setPhone] = useState({ value: '', formattedValue: '', error: '' });
  const [email, setEmail] = useState({ value: '', error: '' });
  const [streetAddress, setStreetAddress] = useState({ value: '', error: '' });
  const [city, setCity] = useState({ value: '', error: '' });
  const [state, setState] = useState({ value: '', error: '' });
  const [zip, setZip] = useState({ value: '', error: '' });
  const [birthDate, setBirthDate] = useState({ value: '', formattedValue: null, error: '' });
  const [gender, setGender] = useState('male');
  const [language, setLanguage] = useState('en');
  const [mode, setMode] = useState('auto');
  const [trigger, setTrigger] = useState('new_patient');
  const [orchestrate, setOrchestrate] = useState(false);

  const handleCreate = () => {
    let hasError = false;

    if (checkEmptyString(email.value)) {
      if (!checkValidEmail(email.value)) {
        setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
        hasError = true;
      }
    }
    if (phone.error) hasError = true;
    if (hasError) return;

    const patient = { 
      ...(firstName.value && { first_name: firstName.value }),
      ...(lastName.value && { last_name: lastName.value }),
      ...(phone.value && { phone: phone.value }),
      ...(email.value && { email: email.value }),
      ...(streetAddress.value && { street_address: streetAddress.value }),
      ...(city.value && { city: city.value }),
      ...(state.value && { state: state.value }),
      ...(zip.value && { zip: zip.value }),
      ...(birthDate.value && { birth_at: getDateWithNullTimeZone(birthDate.value) }),
      gender,
      language,
      mode, 
      trigger, 
      orchestrate
    };

    createPatient({ patient })
    .then(({ payload, error }) => {
      if (!error) {
        const { patient: { id, source } } = payload;
        setSuccess('Patient successfully created');
        onClose();
        if (!filter || filter === source) navigate(`/patients/${id}`);
      }   
    })
  };
  const handleExited = () => { 
    setFirstName({ value: '', error: '' });
    setLastName({ value: '', error: '' });
    setPhone({ value: '', formattedValue: '', error: '' });
    setEmail({ value: '', error: '' });
    setStreetAddress({ value: '', error: '' });
    setCity({ value: '', error: '' });
    setState({ value: '', error: '' });
    setZip({ value: '', error: '' });
    setBirthDate({ value: '', formattedValue: null, error: '' });
    setGender('male');
    setLanguage('en');
    setMode('auto');
    setTrigger('new_patient');
    setOrchestrate(false);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      otherPaperStyle={{ width: '100%', maxWidth: 600 }}
      title='Create Patient'
      TransitionProps={{ onExited: handleExited }}
      actions={
        <>
          <Button
            disabled={fetching}
            variant='outlined'
            onClick={handleCreate}
            sx={{ fontSize: '12px', px: '28px' }}
          >
            Create
          </Button>
        </>
      }
    >
      <Box sx={{ px: 4, pb: 3 }}>
        <TextField
          fullWidth
          margin="dense"
          id="firstName"
          label="First name"
          value={firstName.value}
          onChange={({ target: { value } }) => setFirstName({ value, error: '' })}
          error={!!firstName.error}
          helperText={firstName.error}
        />
        <TextField
          fullWidth
          margin="dense"
          id="lastName"
          label="Last name"
          value={lastName.value}
          onChange={({ target: { value } }) => setLastName({ value, error: '' })}
          error={!!lastName.error}
          helperText={lastName.error}
        />
        <PhoneField
          id='phone'
          sx={{ mt: '16px', mb: '8px' }}
          fullWidth
          margin='dense'
          label='Phone'
          formattedValue={phone.formattedValue}
          error={!!phone.error}
          helperText={phone.error}
          onChange={({ value, formattedValue, error }) => setPhone({ value, formattedValue, error })}
        />
        <TextField
          fullWidth
          margin='dense'
          label='Email'
          disabled={fetching}
          value={email.value}
          onChange={({ target: { value } }) => setEmail({ value, error: '' })}
          helperText={email.error}
          error={!!email.error}
        />
        <TextField
          fullWidth
          margin="dense"
          id="street_address"
          label="Street Address"
          value={streetAddress.value}
          onChange={({ target: { value } }) => setStreetAddress({ value, error: '' })}
          error={!!streetAddress.error}
          helperText={streetAddress.error}
        />
        <TextField
          fullWidth
          margin="dense"
          id="city"
          label="City"
          value={city.value}
          onChange={({ target: { value } }) => setCity({ value, error: '' })}
          error={!!city.error}
          helperText={city.error}
        />
        <TextField
          fullWidth
          margin="dense"
          id="state"
          label="State"
          inputProps={{ maxLength: 2 }}
          value={state.value}
          onChange={({ target: { value } }) => setState({ value, error: '' })}
          error={!!state.error}
          helperText={state.error}
        />
        <TextField
          fullWidth
          margin="dense"
          id="zip"
          label="Zip"
          value={zip.value}
          onChange={({ target: { value } }) => setZip({ value, error: '' })}
          error={!!zip.error}
          helperText={zip.error}
        />
        <BaseDatePicker
          fullWidth
          margin="dense"
          label="Birth Date"
          maxDate={today}
          minDate={
            new Date(new Date().setFullYear(today.getFullYear() - 100))
          }
          value={birthDate.formattedValue}
          error={!!birthDate.error}
          helperText={birthDate.error}
          onChange={({ value, formattedValue, error }) =>
            setBirthDate({ value, formattedValue, error })
          }
        />
        <TextField
          fullWidth
          margin="dense"
          id="gender"
          label="Select gender"
          select
          value={gender}
          onChange={({ target: { value } }) => setGender(value)}
        >
          <MenuItem key={'male'} value={'male'}>Male</MenuItem>
          <MenuItem key={'female'} value={'female'}>Female</MenuItem>
          <MenuItem key={'other'} value={'other'}>Other</MenuItem>
        </TextField>
        <TextField
          fullWidth
          margin='dense'
          select
          label='Language'
          value={language}
          onChange={({ target: { value } }) => setLanguage(value)}
        >
          {_map(languages, ac => <MenuItem key={ac} value={ac}>{ac}</MenuItem>)}
        </TextField>
        <TextField
          fullWidth
          margin='dense'
          select
          label='Select Mode'
          value={mode}
          onChange={({ target: { value } }) => setMode(value)}
        >
          {_map(modes, m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField
          fullWidth
          margin='dense'
          select
          label='Select Trigger'
          value={trigger}
          onChange={({ target: { value } }) => setTrigger(value)}
        >
          {_map(triggers, t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          id="gender"
          label="Select Orchestrate"
          select
          value={orchestrate}
          onChange={({ target: { value } }) => setOrchestrate(value)}
        >
          <MenuItem key={'on'} value={true}>On</MenuItem>
          <MenuItem key={'off'} value={false}>Off</MenuItem>
        </TextField>
      </Box>
    </BaseDialog>
  );
};

export default memo(Patient);